import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import Breadcrumbs from '../../components/ui/breadcrumbs';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import Label from '../../components/ui/label';
import { Edit, Trash2, Plus, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from '../../utils/axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../styles/quill-editor.css';

const AdminBlogPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // all, published, draft
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: '',
    tags: '',
    featured_image: null,
    is_published: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Quill editor configurations
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image', 'video'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean']
    ],
    clipboard: {
      matchVisual: false,
    }
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'video',
    'color', 'background',
    'align'
  ];

  const fetchPosts = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await axios.get('/admin/posts', { params });

      // Handle paginated response
      let postsData = [];
      if (response.data?.posts?.data) {
        // Paginated response
        postsData = response.data.posts.data;
      } else if (response.data?.posts) {
        // Direct array response
        postsData = response.data.posts;
      } else if (response.data?.data) {
        // Alternative structure
        postsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        // Direct array
        postsData = response.data;
      }

      setPosts(Array.isArray(postsData) ? postsData : []);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      toast.error('Failed to fetch posts');
      setPosts([]); // Ensure posts is always an array
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]); // Re-fetch when fetchPosts changes

  useEffect(() => {

    // Add custom image upload handler to Quill
    if (typeof window !== 'undefined') {
      const Quill = ReactQuill.Quill;
      const quillImageHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
          const file = input.files[0];
          if (file) {
            try {
              // Create a FormData instance
              const formData = new FormData();
              formData.append('image', file);

              // Upload the image
              const response = await axios.post('/admin/upload-image', formData, {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              });

              // Get the image URL from the response
              const imageUrl = response.data.url;

              // Get the Quill editor instance
              const quill = document.querySelector('.ql-editor').parentNode.querySelector('.ql-editor').__quill;

              // Get the current cursor position
              const range = quill.getSelection();

              // Insert the image at the cursor position
              quill.insertEmbed(range.index, 'image', imageUrl);

              // Move the cursor after the image
              quill.setSelection(range.index + 1);

              toast.success('Image uploaded successfully');
            } catch (error) {
              console.error('Image upload failed:', error);
              toast.error('Failed to upload image');
            }
          }
        };
      };

      // Register the image handler
      if (Quill) {
        const toolbar = Quill.import('modules/toolbar');
        toolbar.DEFAULTS.handlers = {
          ...toolbar.DEFAULTS.handlers,
          image: quillImageHandler
        };
      }
    }
  }, []);



  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormErrors({});

    try {
      // Prepare data for submission
      const dataToSend = {
        title: formData.title,
        content: formData.content,
        excerpt: formData.excerpt,
        category: formData.category,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        is_published: Boolean(formData.is_published)
      };

      // Handle file upload separately if needed
      if (formData.featured_image) {
        const formDataToSend = new FormData();
        Object.keys(dataToSend).forEach(key => {
          if (key === 'tags') {
            formDataToSend.append(key, JSON.stringify(dataToSend[key]));
          } else {
            formDataToSend.append(key, dataToSend[key]);
          }
        });
        formDataToSend.append('featured_image', formData.featured_image);

        if (editingPost) {
          await axios.put(`/admin/posts/${editingPost.id}`, formDataToSend, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Post updated successfully');
        } else {
          await axios.post('/admin/posts', formDataToSend, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Post created successfully');
        }
      } else {
        // Send as JSON if no file upload
        if (editingPost) {
          await axios.put(`/admin/posts/${editingPost.id}`, dataToSend);
          toast.success('Post updated successfully');
        } else {
          await axios.post('/admin/posts', dataToSend);
          toast.success('Post created successfully');
        }
      }

      setShowForm(false);
      setEditingPost(null);
      setFormData({
        title: '',
        content: '',
        excerpt: '',
        category: '',
        tags: '',
        featured_image: null,
        is_published: false
      });
      fetchPosts();
    } catch (error) {
      console.error('Blog post submission error:', error);

      // Handle validation errors
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
        const firstError = Object.values(error.response.data.errors)[0][0];
        toast.error(firstError);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.status === 401) {
        toast.error('You are not authorized to perform this action');
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else {
        toast.error('Failed to save post. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      category: post.category || '',
      tags: post.tags ? post.tags.join(', ') : '',
      featured_image: null,
      is_published: post.is_published
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await axios.delete(`/admin/posts/${id}`);
        toast.success('Post deleted successfully');
        fetchPosts();
      } catch (error) {
        toast.error('Failed to delete post');
      }
    }
  };

  const togglePublish = async (id) => {
    try {
      await axios.post(`/admin/posts/${id}/toggle-publish`);
      toast.success('Post status updated');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to update post status');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6"><Breadcrumbs /></div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Blog Posts</h1>
          <div className="flex gap-2 mt-4">
            <Button
              variant={statusFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('all')}
            >
              All Posts
            </Button>
            <Button
              variant={statusFilter === 'published' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('published')}
            >
              Published
            </Button>
            <Button
              variant={statusFilter === 'draft' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('draft')}
            >
              Drafts
            </Button>
          </div>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          New Post
        </Button>
      </div>

      {showForm && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{editingPost ? 'Edit Post' : 'Create New Post'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className={formErrors.title ? 'border-red-500' : ''}
                  required
                />
                {formErrors.title && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.title[0]}</p>
                )}
              </div>

              <div>
                <Label htmlFor="excerpt">Excerpt</Label>
                <textarea
                  id="excerpt"
                  value={formData.excerpt}
                  onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                  className="w-full h-20 p-3 border rounded-md"
                  placeholder="Brief description of the post..."
                />
              </div>

              <div>
                <Label htmlFor="content">Content</Label>
                <div className={`${formErrors.content ? 'border border-red-500 rounded-md' : ''}`}>
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={(content) => setFormData({...formData, content})}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Write your post content here..."
                    className="min-h-[300px] mb-12" // Add bottom margin for toolbar
                  />
                </div>
                {formErrors.content && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.content[0]}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    placeholder="e.g., Technology, AI, etc."
                  />
                </div>

                <div>
                  <Label htmlFor="tags">Tags (comma separated)</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData({...formData, tags: e.target.value})}
                    className={formErrors.tags ? 'border-red-500' : ''}
                    placeholder="ai, machine learning, tech"
                  />
                  {formErrors.tags && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.tags[0]}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="featured_image">Featured Image</Label>
                <input
                  id="featured_image"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormData({...formData, featured_image: e.target.files[0]})}
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_published"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
                />
                <Label htmlFor="is_published">Publish immediately</Label>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {editingPost ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingPost ? 'Update Post' : 'Create Post'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingPost(null);
                    setFormData({
                      title: '',
                      content: '',
                      excerpt: '',
                      category: '',
                      tags: '',
                      featured_image: null,
                      is_published: false
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {posts.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="text-gray-500">
                <h3 className="text-lg font-medium mb-2">No blog posts yet</h3>
                <p className="mb-4">Get started by creating your first blog post.</p>
                <Button
                  onClick={() => setShowForm(true)}
                  className="flex items-center gap-2 mx-auto"
                >
                  <Plus className="h-4 w-4" />
                  Create Your First Post
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          posts.map((post) => (
            <Card key={post.id}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">{post.title}</h3>
                    <p className="text-gray-600 mb-2">{post.excerpt}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>By {post.author?.name || 'Unknown'}</span>
                      <span>{new Date(post.created_at).toLocaleDateString()}</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        post.is_published
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {post.is_published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => togglePublish(post.id)}
                    >
                      {post.is_published ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(post)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(post.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminBlogPosts;
